import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import MuiButton from "@material-ui/core/Button";

import { getEstimateHistory } from "services/estimate-history.service";

// Estimate history panel (2026-10-24, job-profit/estimate-history/README.md): when the estimate link was opened,
// signing, staff edits (collapsed) and price changes, from the audit log. Times are Central (CT). Every logged-in user.
// Deep links: /estimates/:id/wizard?history=1 (or #history) opens it expanded; /estimates/:id/history is the full page.

const KIND_STYLE: { [k: string]: React.CSSProperties } = {
  open:       { color: "#1b5e20", fontWeight: 600 },
  signed:     { color: "#0d47a1", fontWeight: 700 },
  staff:      { color: "#424242" },
  staff_view: { color: "#9e9e9e", fontStyle: "italic" },
  automated:  { color: "#9e9e9e", fontStyle: "italic" }
};
const DOT: { [k: string]: string } = { open: "#43a047", signed: "#1e88e5", staff: "#bdbdbd", staff_view: "#e0e0e0", automated: "#e0e0e0" };

export const historyPageUrl = (id: number | string) => `${window.location.origin}/estimates/${id}/history`;

const timesOf = (e: any) => e.span || (e.times || []).map((t: any) => t.time + (t.note ? ` (${t.note})` : "")).join(", ");

export function HistoryTimeline({ data }: { data: any }) {
  if (!data.days || !data.days.length) return <div style={{ color: "#757575", padding: "8px 0" }}>Nothing recorded yet.</div>;
  return (
    <div data-testid="history-timeline">
      {data.days.map((d: any) => (
        <div key={d.date} style={{ padding: "6px 0", borderTop: "1px solid #eeeeee" }}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 2 }}>{d.label}</div>
          {d.entries.map((e: any, i: number) => (
            <div key={i} style={{ display: "flex", alignItems: "baseline", padding: "2px 0 2px 4px", fontSize: 14, lineHeight: 1.45 }}>
              <span style={{ width: 9, height: 9, borderRadius: 5, background: DOT[e.kind] || "#bdbdbd", flex: "0 0 9px", marginRight: 10, position: "relative", top: 1 }} />
              <span style={{ flex: 1, minWidth: 0, overflowWrap: "anywhere" }}>
                <span style={KIND_STYLE[e.kind] || {}}>{e.text}</span>
                {timesOf(e) && <span style={{ color: "#757575" }}> · {timesOf(e)}</span>}
                {e.price && <div style={{ color: "#5d4037", fontSize: 13 }}>Price: {e.price}</div>}
                {e.status && <div style={{ color: "#757575", fontSize: 13 }}>Status: {e.status}</div>}
                {e.note && <div style={{ color: "#e65100", fontSize: 13 }}>{e.note}</div>}
              </span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

export function HistoryNotes({ data }: { data: any }) {
  if (!data.notes || !data.notes.length) return null;
  return (
    <div style={{ color: "#757575", fontSize: 12, marginTop: 8 }} data-testid="history-notes">
      {data.notes.map((n: string, i: number) => <div key={i} style={{ marginTop: 2 }}>{n}</div>)}
    </div>
  );
}

export default function EstimateHistoryPanel({ estimateId, initiallyOpen }: { estimateId: number; initiallyOpen?: boolean }) {
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(!!initiallyOpen);
  const [copied, setCopied] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    getEstimateHistory(estimateId).then(setData).catch(() => setError("Could not load the estimate history."));
  }, [estimateId]);

  useEffect(() => { if (initiallyOpen) setOpen(true); }, [initiallyOpen]);
  useEffect(() => {
    if (open && initiallyOpen && data && ref.current) ref.current.scrollIntoView({ block: "start" });
  }, [open, initiallyOpen, data]);

  const copyLink = () => {
    const url = historyPageUrl(estimateId);
    const done = () => { setCopied(true); setTimeout(() => setCopied(false), 2000); };
    if ((navigator as any).clipboard) (navigator as any).clipboard.writeText(url).then(done, () => window.prompt("Copy this link", url));
    else window.prompt("Copy this link", url);
  };

  return (
    <div id="history" ref={ref} data-testid="estimate-history"
         style={{ background: "#fff", borderLeft: "4px solid #1e88e5", boxShadow: "0 1px 3px rgba(0,0,0,0.12)", borderRadius: 4,
                  padding: "10px 14px", margin: "8px 0 12px" }}>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center" }}>
        <span style={{ fontWeight: 700, fontSize: 15, marginRight: 10 }}>Estimate history</span>
        <span style={{ flex: "1 1 300px", fontSize: 14, color: "#212121", minWidth: 0 }} data-testid="history-summary">
          {error || (data ? data.summaryLine : "Loading...")}
        </span>
        <span style={{ whiteSpace: "nowrap" }}>
          <MuiButton size="small" color="primary" onClick={() => setOpen(!open)} disabled={!data} data-testid="history-toggle">
            {open ? "Hide history" : "Show history"}
          </MuiButton>
          <MuiButton size="small" onClick={copyLink} title="Copy the link to this history page (staff login needed)">
            {copied ? "Copied" : "Copy link"}
          </MuiButton>
        </span>
      </div>
      {open && data && (
        <div style={{ marginTop: 6 }}>
          <HistoryTimeline data={data} />
          <div style={{ fontSize: 12, color: "#757575", marginTop: 6 }}>
            <Link to={`/estimates/${estimateId}/history`}>Open as a full page</Link>
          </div>
          <HistoryNotes data={data} />
        </div>
      )}
    </div>
  );
}
